import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { MessageCircle, ChevronLeft, RefreshCw } from "lucide-react";

export default function ChatTranscripts() {
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/dealer/chats");
      setSessions(data.items || []);
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSession = async (sid) => {
    setActive(sid);
    setTranscript(null);
    const { data } = await api.get(`/dealer/chats/${sid}`);
    setTranscript(data);
  };

  if (active && transcript) {
    return (
      <div data-testid="dealer-chat-transcript">
        <button onClick={() => { setActive(null); setTranscript(null); }} className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4" data-testid="transcript-back">
          <ChevronLeft size={16} /> Back to conversations
        </button>
        <div className="bg-white rounded-2xl overflow-hidden">
          {/* Lead header (if captured) */}
          {transcript.lead ? (
            <div className="bg-[var(--ssc-navy)] text-white px-4 py-3 flex flex-wrap gap-3 items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-silver)]">Lead captured</div>
                <div className="font-extrabold">{transcript.lead.name}</div>
                <div className="text-xs">
                  {transcript.lead.email && <a className="text-[var(--ssc-blue)]" href={`mailto:${transcript.lead.email}`}>{transcript.lead.email}</a>}
                  {transcript.lead.phone && <span> · <a className="text-white" href={`tel:${transcript.lead.phone}`}>{transcript.lead.phone}</a></span>}
                </div>
              </div>
              {transcript.lead.preferred_contact && (
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[var(--ssc-blue)] text-white px-2 py-1 rounded-full">
                  Prefers {transcript.lead.preferred_contact}
                </span>
              )}
            </div>
          ) : (
            <div className="bg-[var(--ssc-paper)] px-4 py-3 text-xs text-[var(--ssc-ink-2)] border-b border-[var(--ssc-line)]">
              No lead captured — visitor only chatted with the bot.
            </div>
          )}

          {/* Transcript */}
          <div className="p-4 max-h-[60vh] overflow-y-auto" data-testid="transcript-body">
            {transcript.messages.length === 0 ? (
              <div className="text-center py-10 text-[var(--ssc-ink-2)] text-sm">No messages.</div>
            ) : (
              <div className="space-y-3">
                {transcript.messages.map((m) => (
                  <div key={m.id || m.created_at} className="flex flex-col gap-2">
                    {m.user && (
                      <div className="self-end max-w-[80%]">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)] mb-1 text-right">Customer · {new Date(m.created_at).toLocaleTimeString()}</div>
                        <div className="ssc-chat-msg ssc-chat-msg-user">{m.user}</div>
                      </div>
                    )}
                    {m.assistant && (
                      <div className="self-start max-w-[80%]">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-blue)] mb-1">Bot</div>
                        <div className="ssc-chat-msg ssc-chat-msg-bot">{m.assistant}</div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dealer-chats">
      <div className="flex items-center justify-between mb-3">
        <div className="text-white/70 text-sm">Most recent chat sessions on the site. Click into a row to read the full transcript.</div>
        <button onClick={load} className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="chats-refresh">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-2xl overflow-hidden">
        {busy && sessions.length === 0 ? (
          <div className="text-center py-12 text-[var(--ssc-ink-2)] text-sm">Loading…</div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 text-[var(--ssc-ink-2)] text-sm" data-testid="chats-empty">
            No chat sessions yet. When customers chat with the bot, they'll show up here in real time.
          </div>
        ) : (
          <ul className="divide-y divide-[var(--ssc-line)]" data-testid="chats-list">
            {sessions.map((s) => (
              <li key={s.session_id}
                  onClick={() => openSession(s.session_id)}
                  className="px-4 py-3 hover:bg-[var(--ssc-paper)] cursor-pointer transition-colors"
                  data-testid={`chat-row-${s.session_id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[var(--ssc-paper)] text-[var(--ssc-blue)] flex items-center justify-center">
                    <MessageCircle size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-[var(--ssc-ink)] text-sm truncate">
                        {s.lead?.name || "Anonymous visitor"}
                      </span>
                      {s.lead && <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border bg-emerald-50 border-emerald-200 text-emerald-700">Lead captured</span>}
                      <span className="text-xs text-[var(--ssc-ink-2)]">{s.messages} msgs</span>
                    </div>
                    {s.lead && (
                      <div className="text-xs text-[var(--ssc-ink-2)] truncate">
                        {s.lead.email}{s.lead.phone ? ` · ${s.lead.phone}` : ""}
                      </div>
                    )}
                    {s.last_user_message && (
                      <div className="text-sm text-[var(--ssc-ink)] truncate">"{s.last_user_message}"</div>
                    )}
                  </div>
                  <div className="text-xs text-[var(--ssc-ink-2)] shrink-0">
                    {new Date(s.last_at).toLocaleString()}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
